import React, { useState, useEffect } from 'react';
import { Eraser, Trash2, MessageSquare, ChevronRight } from 'lucide-react';
import { FONT, BLUE, TEXT, TEXT2, TEXT3, BORDER } from '../shared';
import ShiningText from './ui/ShiningText';
import EditToolbar from './EditToolbar';

// ─────────────────────────────────────────────────────────────────────────
// ResultPreviewPanel — right-docked, frosted-glass split preview (Claude
// Design "split preview"). Chat stays left (40%), this panel is the right 60%.
// Sizing matches the Claude Design spec exactly:
//   • 52px header (title 16px semibold · Preview/Code tabs · close X)
//   • main preview fills remaining space (iframe / image, min-height 400)
//   • a far-right control sidebar (~200–220px) whose sections depend on type
//   • bottom action bar (Regenerate outline · Publish/Launch filled, right)
//
// Control sidebars by preview type:
//   • website → LAYOUT (Desktop/Mobile) · Font (Inter/Mono) · EDIT helper
//   • image   → PROMPT · ASPECT RATIO · STYLE · VARIATIONS
//   • ad      → CREATIVE grid · platform icons · FORMAT · BUTTON COLOR · rewrite
//
// Visual-only — every action is delegated to the caller.
//
// Props:
//   payload  { kind, previewType?, title, url?, html?, image?, caption?,
//              prompt?, cta?, variations?:string[] }
//   width    CSS width string (also used by the caller to pad the composer)
//   onClose · onRegenerate · onSelectVariation · onRewriteCopy · onChip · onAction
// ─────────────────────────────────────────────────────────────────────────

const ASPECTS = [
  { id: '1:1',  label: '1:1',  ratio: '1 / 1'  },
  { id: '4:5',  label: '4:5',  ratio: '4 / 5'  },
  { id: '16:9', label: '16:9', ratio: '16 / 9' },
  { id: '9:16', label: '9:16', ratio: '9 / 16' },
];

const STYLES = ['Photographic', '3D Render', 'Illustration', 'Cinematic'];

const AD_FORMATS = [
  { id: 'feed',  label: 'Feed 4:5'  },
  { id: 'story', label: 'Story 9:16' },
];

const PLATFORMS = [
  { id: 'meta',   label: 'Meta',   short: 'M', color: '#1877F2' },
  { id: 'google', label: 'Google', short: 'G', color: '#EA4335' },
  { id: 'tiktok', label: 'TikTok', short: 'T', color: '#111111' },
];

const BUTTON_COLORS = [
  { id: 'ocean',  hex: '#275C73' },
  { id: 'blue',   hex: '#0EA5E9' },
  { id: 'green',  hex: '#10B981' },
  { id: 'violet', hex: '#7C3AED' },
  { id: 'amber',  hex: '#F59E0B' },
  { id: 'rose',   hex: '#F43F5E' },
  { id: 'slate',  hex: '#1F2937' },
];

const TYPEFACES = [
  { id: 'inter',   label: 'Inter' },
  { id: 'georgia', label: 'Georgia' },
  { id: 'mono',    label: 'Mono' },
];

// Pull up to 6 distinct accent colors out of the generated HTML so the
// palette swatches reflect the actual site, not a fixed list. Skips
// near-white / near-black so the swatches read as brand accents.
function extractPalette(html) {
  if (!html || typeof html !== 'string') return [];
  const out = [];
  const seen = new Set();
  const re = /#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g;
  let m;
  while ((m = re.exec(html)) && out.length < 6) {
    let hex = m[1];
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    hex = '#' + hex.toLowerCase();
    if (seen.has(hex)) continue;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const lum = (r + g + b) / 3;
    if (lum > 244 || lum < 14) continue; // skip near-white / near-black
    seen.add(hex);
    out.push(hex);
  }
  return out;
}

// Suggest a buyable domain from the site title (e.g. "Lumen" → "lumen.com").
function suggestDomain(title) {
  const slug = (title || 'yoursite')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 24) || 'yoursite';
  return `${slug}.com`;
}

// Frosted glass per spec: blur(40) saturate(180), translucent white.
const GLASS = {
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
};

const RADIUS_CARD = 18;
const RADIUS_CTRL = 10;

// Section label — 11px uppercase, tracking 0.04em, weight 500, muted.
function Label({ children }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: TEXT3 }}>
      {children}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Label>{title}</Label>
      {children}
    </div>
  );
}

// Header / tab pill (14px).
function TabPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 14, fontWeight: 600,
        padding: '5px 14px', borderRadius: RADIUS_CTRL,
        color: active ? '#fff' : TEXT2, background: active ? BLUE : 'transparent',
        transition: 'background 160ms var(--ds-ease-out), color 160ms var(--ds-ease-out)',
      }}
    >
      {children}
    </button>
  );
}

// Segmented control (Desktop/Mobile, Inter/Mono, FORMAT). 32–36px tall.
function Segmented({ options, value, onChange, height = 32 }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              flex: 1, height, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600,
              borderRadius: RADIUS_CTRL,
              color: active ? '#fff' : TEXT2,
              background: active ? BLUE : 'rgba(255,255,255,0.6)',
              border: active ? `1px solid ${BLUE}` : `1px solid ${BORDER}`,
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// 64×64 thumbnail grid — 2 columns, 8px gap, rounded 8.
function ThumbGrid({ images, activeIdx, onSelect }) {
  if (!images.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 64px)', gap: 8 }}>
      {images.slice(0, 6).map((src, i) => (
        <button
          key={i}
          type="button"
          aria-label={`Variation ${i + 1}`}
          onClick={() => onSelect(i)}
          style={{
            width: 64, height: 64, padding: 0, cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
            background: 'rgba(255,255,255,0.5)',
            border: i === activeIdx ? `2px solid ${BLUE}` : `1px solid ${BORDER}`,
            boxShadow: i === activeIdx ? '0 6px 16px -8px rgba(39,92,115,0.5)' : 'none',
            transition: 'border-color 160ms ease',
          }}
        >
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </button>
      ))}
    </div>
  );
}

// Footer action button (right-aligned, auto width).
function FooterBtn({ primary, height = 40, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="liquid-press"
      style={{
        height, padding: '0 22px', cursor: 'pointer', fontFamily: FONT, fontSize: 14, fontWeight: 600,
        borderRadius: RADIUS_CTRL,
        color: primary ? '#fff' : TEXT,
        background: primary ? BLUE : 'rgba(255,255,255,0.6)',
        border: primary ? `1px solid ${BLUE}` : `1px solid ${BORDER}`,
        boxShadow: primary ? '0 8px 20px -8px rgba(39,92,115,0.5)' : 'inset 0 1px 0 rgba(255,255,255,0.85)',
        transition: 'filter 160ms ease',
      }}
    >
      {children}
    </button>
  );
}

// Freehand markup overlay — draws over the preview on a transparent canvas.
// Local-only (cleared when Mark up is toggled off); persistence would need a
// backend endpoint to store annotations per site.
function MarkupCanvas() {
  const ref = React.useRef(null);
  const drawing = React.useRef(false);
  React.useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const parent = cv.parentElement;
    const resize = () => { if (parent) { cv.width = parent.clientWidth; cv.height = parent.clientHeight; } };
    resize();
    const ctx = cv.getContext('2d');
    ctx.strokeStyle = '#f43f5e';
    ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    const at = (e) => { const r = cv.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    const down = (e) => { drawing.current = true; const [x, y] = at(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing.current) return; const [x, y] = at(e); ctx.lineTo(x, y); ctx.stroke(); };
    const up = () => { drawing.current = false; };
    cv.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('resize', resize);
    return () => {
      cv.removeEventListener('mousedown', down);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, zIndex: 4, cursor: 'crosshair' }} />;
}

export default function ResultPreviewPanel({ payload, width = '60vw', onClose, onRegenerate, onSelectVariation, onRewriteCopy, onChip, onAction }) {
  const [tab, setTab] = useState('preview');
  const [device, setDevice] = useState('desktop');
  const [font, setFont] = useState('inter');
  const [aspect, setAspect] = useState('1:1');
  const [style, setStyle] = useState('Photographic');
  const [adFormat, setAdFormat] = useState('feed');
  const [platform, setPlatform] = useState('meta');
  const [btnColor, setBtnColor] = useState(BUTTON_COLORS[0].id);
  const [activeIdx, setActiveIdx] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [accent, setAccent] = useState(null);
  const [domain, setDomain] = useState('');
  const [logoName, setLogoName] = useState('');
  const logoInputRef = React.useRef(null);

  // ── Toolbar-driven preview tools (EditToolbar → vella:toolbar events) ──
  const frameWrapRef = React.useRef(null); // element we fullscreen + scale
  const iframeRef = React.useRef(null);
  const [markup, setMarkup] = useState(false);
  const [markupKey, setMarkupKey] = useState(0); // bump to remount canvas = Clear
  const [commentMode, setCommentMode] = useState(false);
  // Pins are LOCAL STATE ONLY — persisting them needs a backend endpoint
  // (e.g. POST /api/preview-comments keyed by conversation + result id).
  const [pins, setPins] = useState([]);        // {id, x, y, text, timestamp} — x/y are % of frame
  const [draftPin, setDraftPin] = useState(null);
  const [pinListOpen, setPinListOpen] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [fontScale, setFontScale] = useState(100);
  const [tweakText, setTweakText] = useState('');
  const [frameLoading, setFrameLoading] = useState(true);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const presentPreview = React.useCallback((mode) => {
    if (mode === 'fullscreen') {
      const el = frameWrapRef.current;
      if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } else if (mode === 'newtab') {
      if (payload?.html) {
        const url = URL.createObjectURL(new Blob([payload.html], { type: 'text/html' }));
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else if (payload?.url) {
        window.open(payload.url, '_blank', 'noopener');
      }
    } else if (mode === 'share' || mode === 'copylink') {
      const link = payload?.url;
      if (link && navigator.clipboard) {
        navigator.clipboard.writeText(link)
          .then(() => setToast('Copied!'))
          .catch(() => setToast('Could not copy the link.'));
      } else {
        setToast(link ? 'Clipboard unavailable in this browser.' : 'No live link yet — publish the site first.');
      }
    }
  }, [payload]);

  useEffect(() => {
    const handler = (e) => {
      const d = (e && e.detail) || {};
      switch (d.action) {
        case 'markup':   setMarkup(!!d.active); if (d.active) setCommentMode(false); break;
        case 'comments': setCommentMode(!!d.active); if (d.active) setMarkup(false); break;
        case 'edit':     setEditMode((cur) => (typeof d.active === 'boolean' ? d.active : !cur)); break;
        case 'tweaks':   setTweaksOpen(!!d.active); break;
        case 'zoom':     setZoom((Number(d.value) || 100) / 100); break;
        case 'present':  presentPreview(d.value); break;
        default: break;
      }
    };
    window.addEventListener('vella:toolbar', handler);
    return () => window.removeEventListener('vella:toolbar', handler);
  }, [presentPreview]);

  // Best-effort font-size tweak injected into the srcDoc iframe (same-origin).
  useEffect(() => {
    try {
      const doc = iframeRef.current && iframeRef.current.contentDocument;
      if (doc && doc.documentElement) doc.documentElement.style.fontSize = `${fontScale}%`;
    } catch { /* not ready / blocked — ignore */ }
  }, [fontScale, frameLoading]);

  // Edit mode — make the site's text inline-editable (srcDoc is same-origin,
  // so we can flip contentEditable directly; no bridge script required).
  const EDIT_SEL = 'h1,h2,h3,h4,h5,h6,p,span,a,li,button,td,th,figcaption,blockquote,label';
  useEffect(() => {
    try {
      const doc = iframeRef.current && iframeRef.current.contentDocument;
      if (!doc || !doc.body) return;
      doc.body.dataset.vellaEdit = editMode ? '1' : '0';
      doc.querySelectorAll(EDIT_SEL).forEach((el) => {
        if (editMode) {
          el.setAttribute('contenteditable', 'true');
          el.style.outline = '1px dashed rgba(39,92,115,0.45)';
          el.style.outlineOffset = '2px';
          el.style.cursor = 'text';
        } else {
          el.removeAttribute('contenteditable');
          el.style.outline = '';
          el.style.outlineOffset = '';
          el.style.cursor = '';
        }
      });
      // While editing, clicking a link should edit its text, not navigate.
      if (editMode && !doc.__vellaEditGuard) {
        doc.__vellaEditGuard = true;
        doc.addEventListener('click', (e) => {
          if (doc.body && doc.body.dataset.vellaEdit === '1') {
            const a = e.target.closest && e.target.closest('a');
            if (a) e.preventDefault();
          }
        }, true);
      }
    } catch { /* cross-origin src — inline editing unavailable */ }
  }, [editMode, frameLoading]);

  // Belt-and-braces for the loading veil: fast srcDoc paints (and remounts)
  // can finish before React attaches onLoad, so the load event never fires
  // for us — poll readyState briefly and drop the veil regardless after 2.5s.
  useEffect(() => {
    if (!frameLoading) return;
    const check = () => {
      try {
        const doc = iframeRef.current && iframeRef.current.contentDocument;
        if (doc && doc.readyState === 'complete' && doc.body && doc.body.childNodes.length) {
          setFrameLoading(false);
          return true;
        }
      } catch { /* cross-origin src — rely on onLoad / timeout */ }
      return false;
    };
    if (check()) return;
    const iv = setInterval(() => { if (check()) clearInterval(iv); }, 200);
    const to = setTimeout(() => { clearInterval(iv); setFrameLoading(false); }, 2500);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [frameLoading, payload?.html, payload?.url]);

  const images = (payload?.variations && payload.variations.length)
    ? payload.variations
    : (payload?.image ? [payload.image] : []);

  // Reset transient controls when the result changes (panel isn't re-keyed).
  useEffect(() => {
    setActiveIdx(0); setAspect('1:1'); setTab('preview');
    setPrompt(payload?.prompt || payload?.caption || '');
    setDomain(suggestDomain(payload?.title));
    setAccent(null);
    setLogoName('');
    setFrameLoading(true);
    setPins([]); setDraftPin(null);
    // Overlays are anchored to the old document — drop them with it.
    setMarkup(false); setCommentMode(false); setEditMode(false);
  }, [payload?.image, payload?.title, payload?.prompt, payload?.caption, payload?.html, payload?.url]);

  if (!payload) return null;

  const isWebsite = payload.kind === 'website';
  const previewType = payload.previewType || (isWebsite ? 'website' : 'image');
  const isAd = previewType === 'ad';
  const title = payload.title || (isWebsite ? 'New Website' : 'New Creative');
  const hasHtml = !!payload.html;
  const frameMaxW = device === 'mobile' ? 390 : '100%';
  const activeImage = images[Math.min(activeIdx, images.length - 1)] || payload.image;
  const sidebarW = isWebsite ? 280 : 220;
  const palette = isWebsite ? extractPalette(payload.html) : [];

  const selectVariation = (i) => { setActiveIdx(i); onSelectVariation && onSelectVariation(i); };

  return (
    <aside
      aria-label={`${title} preview`}
      style={{
        position: 'fixed', top: 60, right: 0, bottom: 0, width, zIndex: 40,
        display: 'flex', flexDirection: 'column', fontFamily: FONT,
        borderLeft: `1px solid ${BORDER}`,
        boxShadow: '-12px 0 40px -20px rgba(39,92,115,0.30)',
        animation: 'slideUp 0.28s ease',
        ...GLASS,
      }}
    >
      {/* Header — 52px · title · tabs · close */}
      <div style={{ flex: '0 0 auto', height: 52, display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: TEXT, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>{title}</span>
        {isWebsite && (
          <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: RADIUS_CTRL, background: 'rgba(255,255,255,0.5)', border: `1px solid ${BORDER}` }}>
            <TabPill active={tab === 'preview'} onClick={() => setTab('preview')}>Preview</TabPill>
            <TabPill active={tab === 'code'} onClick={() => setTab('code')}>Code</TabPill>
          </div>
        )}
        <button
          type="button"
          aria-label="Close preview"
          onClick={onClose}
          style={{ width: 32, height: 32, flexShrink: 0, borderRadius: RADIUS_CTRL, border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.5)', color: TEXT2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      {/* Body — main preview + collapsible control sidebar */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Collapse / expand toggle — rides the seam between preview & sidebar */}
        <button
          type="button"
          aria-label={sidebarOpen ? 'Collapse controls' : 'Expand controls'}
          title={sidebarOpen ? 'Collapse controls' : 'Expand controls'}
          onClick={() => setSidebarOpen(o => !o)}
          style={{
            position: 'absolute', top: 14, zIndex: 2,
            right: sidebarOpen ? sidebarW - 13 : -1,
            width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.9)', color: TEXT2,
            boxShadow: '0 4px 12px -4px rgba(39,92,115,0.3)',
            transition: 'right 280ms var(--ds-ease-out)',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: sidebarOpen ? 'none' : 'rotate(180deg)' }}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Main preview area — fixed box, 16px padding; the iframe scrolls
            internally so there are never double scrollbars. `position:relative`
            so the Edit toolbar can dock top-center OVER this preview frame
            (D9 — it used to render over the chat rail in the left pane). */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden', padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
          {/* Edit toolbar — docked top-center of the preview frame. */}
          <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 12 }}>
            <EditToolbar />
          </div>
          {isWebsite ? (
            tab === 'code' ? (
              <pre style={{ width: '100%', alignSelf: 'stretch', margin: 0, padding: 16, borderRadius: RADIUS_CARD, background: 'rgba(20,41,50,0.92)', color: '#cbe7f0', fontFamily: 'var(--ds-font-mono)', fontSize: 12, lineHeight: 1.55, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {payload.html || '<!-- No source available for this preview -->'}
              </pre>
            ) : (
              <div
                ref={frameWrapRef}
                style={{ position: 'relative', width: '100%', maxWidth: frameMaxW, height: '100%', minHeight: 400, borderRadius: 12, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#fff', boxShadow: '0 18px 50px -20px rgba(39,92,115,0.35)', transition: 'max-width 280ms var(--ds-ease-out)' }}
              >
                {/* Zoom wrapper — scales the frame to fit the box at any zoom. */}
                <div style={{ width: `${100 / zoom}%`, height: `${100 / zoom}%`, transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 160ms var(--ds-ease-out)' }}>
                  {hasHtml ? (
                    <iframe ref={iframeRef} onLoad={() => setFrameLoading(false)} title={title} srcDoc={payload.html} style={{ width: '100%', height: '100%', minHeight: 400, border: 'none', display: 'block' }} sandbox="allow-scripts allow-same-origin allow-popups" />
                  ) : payload.url ? (
                    <iframe ref={iframeRef} onLoad={() => setFrameLoading(false)} title={title} src={payload.url} style={{ width: '100%', height: '100%', minHeight: 400, border: 'none', display: 'block' }} sandbox="allow-scripts allow-same-origin allow-popups" />
                  ) : payload.image ? (
                    <img src={payload.image} alt={title} onLoad={() => setFrameLoading(false)} style={{ width: '100%', display: 'block' }} />
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center', color: TEXT3, fontSize: 13 }}>Preview is being prepared…</div>
                  )}
                </div>

                {/* Loading shimmer while the frame paints */}
                {frameLoading && (hasHtml || payload.url) && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', zIndex: 3, pointerEvents: 'none' }}>
                    <ShiningText text="Loading preview..." />
                  </div>
                )}

                {/* Mark up — freehand drawing overlay + Clear */}
                {markup && (
                  <>
                    <MarkupCanvas key={markupKey} />
                    <button
                      type="button"
                      onClick={() => setMarkupKey((k) => k + 1)}
                      style={{ position: 'absolute', top: 10, left: 10, zIndex: 6, display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: TEXT2, background: 'rgba(255,255,255,0.95)', border: `1px solid ${BORDER}`, borderRadius: RADIUS_CTRL, boxShadow: '0 4px 12px -4px rgba(39,92,115,0.3)' }}
                    >
                      <Eraser size={13} strokeWidth={1.8} />
                      Clear
                    </button>
                  </>
                )}

                {/* Comments — click to drop a pin */}
                {commentMode && (
                  <div
                    style={{ position: 'absolute', inset: 0, zIndex: 4, cursor: 'crosshair' }}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setDraftPin({ id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100, text: '' });
                    }}
                  />
                )}
                {pins.map((p, i) => (
                  <div key={p.id} title={p.text} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%,-100%)', zIndex: 5 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50% 50% 50% 0', transform: 'rotate(45deg)', background: BLUE, boxShadow: '0 4px 10px -3px rgba(39,92,115,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ transform: 'rotate(-45deg)', color: '#fff', fontSize: 11, fontWeight: 700 }}>{i + 1}</span>
                    </div>
                  </div>
                ))}
                {draftPin && (
                  <div style={{ position: 'absolute', left: `${draftPin.x}%`, top: `${draftPin.y}%`, transform: 'translate(-50%, 10px)', zIndex: 6, width: 214, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, boxShadow: '0 12px 30px -10px rgba(15,23,42,0.3)', padding: 10 }}>
                    <textarea autoFocus value={draftPin.text} onChange={(e) => setDraftPin({ ...draftPin, text: e.target.value })} placeholder="Add a comment…" rows={2} style={{ width: '100%', boxSizing: 'border-box', resize: 'none', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 8, fontFamily: FONT, fontSize: 13, outline: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                      <button type="button" onClick={() => setDraftPin(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: TEXT3 }}>Cancel</button>
                      <button type="button" onClick={() => { const t = draftPin.text.trim(); if (t) { setPins((ps) => [...ps, { ...draftPin, text: t, timestamp: Date.now() }]); setPinListOpen(true); } setDraftPin(null); }} style={{ border: 'none', background: BLUE, color: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Comment</button>
                    </div>
                  </div>
                )}

                {/* Collapsible comment list — delete pins individually */}
                {commentMode && pins.length > 0 && (
                  <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 7, width: pinListOpen ? 230 : 'auto', maxHeight: '70%', display: 'flex', flexDirection: 'column', borderRadius: 12, background: 'rgba(255,255,255,0.97)', border: `1px solid ${BORDER}`, boxShadow: '0 12px 32px -12px rgba(39,92,115,0.35)', overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setPinListOpen((o) => !o)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: TEXT }}
                    >
                      <MessageSquare size={13} strokeWidth={1.8} />
                      Comments ({pins.length})
                      <ChevronRight size={13} strokeWidth={2} style={{ marginLeft: 'auto', transform: pinListOpen ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }} />
                    </button>
                    {pinListOpen && (
                      <div style={{ overflow: 'auto', padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {pins.map((p, i) => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 8, borderRadius: 8, background: 'rgba(39,92,115,0.06)' }}>
                            <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: '50%', background: BLUE, color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                            <span style={{ flex: 1, fontSize: 12, lineHeight: 1.4, color: TEXT2, wordBreak: 'break-word' }}>{p.text}</span>
                            <button type="button" aria-label={`Delete comment ${i + 1}`} onClick={() => setPins((ps) => ps.filter((x) => x.id !== p.id))} style={{ flexShrink: 0, width: 20, height: 20, padding: 0, border: 'none', background: 'transparent', color: TEXT3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Trash2 size={12} strokeWidth={1.8} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <>
              {/* Image / ad — object-fit contain, centered */}
              <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {activeImage ? (
                  <img src={activeImage} alt={title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: RADIUS_CARD, boxShadow: '0 18px 50px -20px rgba(39,92,115,0.35)', display: 'block' }} />
                ) : (
                  <div style={{ padding: 48, textAlign: 'center', color: TEXT3, fontSize: 13 }}>Creative is being prepared…</div>
                )}
              </div>
              {isAd && payload.cta && (
                <span style={{ display: 'inline-block', padding: '9px 18px', borderRadius: RADIUS_CTRL, background: BUTTON_COLORS.find(c => c.id === btnColor)?.hex || BLUE, color: '#fff', fontSize: 14, fontWeight: 700, transition: 'background 160ms ease' }}>{payload.cta}</span>
              )}
            </>
          )}
        </div>

        {/* Control sidebar — collapsible */}
        <aside style={{ flex: `0 0 ${sidebarOpen ? sidebarW : 0}px`, width: sidebarOpen ? sidebarW : 0, borderLeft: sidebarOpen ? `1px solid ${BORDER}` : 'none', overflow: 'hidden', background: 'rgba(255,255,255,0.4)', transition: 'flex-basis 280ms var(--ds-ease-out), width 280ms var(--ds-ease-out)' }}>
          <div style={{ width: sidebarW, height: '100%', boxSizing: 'border-box', overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 24, opacity: sidebarOpen ? 1 : 0, transition: 'opacity 200ms var(--ds-ease-out)' }}>
          {isWebsite ? (
            <>
              <Section title="Device">
                <Segmented height={32} value={device} onChange={setDevice} options={[{ id: 'desktop', label: 'Desktop' }, { id: 'mobile', label: 'Mobile' }]} />
              </Section>

              <Section title="Brand logo">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) { setLogoName(f.name); onAction && onAction({ type: 'action', action: 'upload_logo', value: f.name }); }
                    if (e.target) e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', height: 38, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT2, background: 'rgba(255,255,255,0.6)', border: `1px dashed ${BORDER}`, borderRadius: RADIUS_CTRL, transition: 'border-color 160ms ease, color 160ms ease' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  {logoName ? 'Logo added' : 'Upload logo'}
                </button>
                {logoName && (
                  <span style={{ fontSize: 11, color: TEXT3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{logoName}</span>
                )}
              </Section>

              {palette.length > 0 && (
                <Section title="Palette">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {palette.map((hex) => (
                      <button
                        key={hex}
                        type="button"
                        aria-label={`Accent ${hex}`}
                        title={hex}
                        onClick={() => { setAccent(hex); onAction && onAction({ type: 'action', action: 'set_accent', value: hex }); }}
                        style={{ width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', background: hex, border: accent === hex ? '2px solid #fff' : '2px solid transparent', boxShadow: accent === hex ? `0 0 0 2px ${hex}` : 'inset 0 0 0 1px rgba(0,0,0,0.08)', transition: 'box-shadow 160ms ease' }}
                      />
                    ))}
                  </div>
                </Section>
              )}

              <Section title="Typeface">
                <Segmented height={32} value={font} onChange={(v) => { setFont(v); onAction && onAction({ type: 'action', action: 'set_typeface', value: v }); }} options={TYPEFACES} />
              </Section>

              <Section title="Domain">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    spellCheck={false}
                    style={{ width: '100%', height: 36, fontFamily: FONT, fontSize: 13, color: TEXT, padding: '0 12px', borderRadius: RADIUS_CTRL, border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.6)', outline: 'none' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: TEXT3 }}>$12<span style={{ fontSize: 11 }}>/yr</span></span>
                    <button
                      type="button"
                      onClick={() => onAction && onAction({ type: 'action', action: 'buy_domain', value: domain })}
                      style={{ height: 32, padding: '0 14px', cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#fff', background: BLUE, border: `1px solid ${BLUE}`, borderRadius: RADIUS_CTRL, boxShadow: '0 6px 16px -8px rgba(39,92,115,0.5)' }}
                    >
                      Buy domain
                    </button>
                  </div>
                </div>
              </Section>

              <Section title="Edit anything">
                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: TEXT3 }}>
                  Scroll the full page and click any text — heading, paragraph, button, stat or footer — to rewrite it. Click any image or logo to upload your own.
                </p>
              </Section>
            </>
          ) : isAd ? (
            <>
              <Section title="Creative">
                <ThumbGrid images={images} activeIdx={activeIdx} onSelect={selectVariation} />
              </Section>
              <Section title="Platform">
                <div style={{ display: 'flex', gap: 8 }}>
                  {PLATFORMS.map(p => {
                    const active = platform === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        aria-label={p.label}
                        title={p.label}
                        onClick={() => { setPlatform(p.id); onAction && onAction({ type: 'action', action: 'set_platform', value: p.id }); }}
                        style={{ width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700, background: p.color, border: active ? '2px solid #fff' : '2px solid transparent', boxShadow: active ? `0 0 0 2px ${p.color}` : 'inset 0 0 0 1px rgba(0,0,0,0.08)', transition: 'box-shadow 160ms ease' }}
                      >
                        {p.short}
                      </button>
                    );
                  })}
                </div>
              </Section>
              <Section title="Format">
                <Segmented height={36} value={adFormat} onChange={(v) => { setAdFormat(v); onAction && onAction({ type: 'action', action: 'set_format', value: v }); }} options={AD_FORMATS} />
              </Section>
              <Section title="Button color">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {BUTTON_COLORS.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      aria-label={`Button color ${c.id}`}
                      onClick={() => { setBtnColor(c.id); onAction && onAction({ type: 'action', action: 'set_button_color', value: c.id }); }}
                      style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', background: c.hex, border: btnColor === c.id ? '2px solid #fff' : '2px solid transparent', boxShadow: btnColor === c.id ? `0 0 0 2px ${c.hex}` : 'inset 0 0 0 1px rgba(0,0,0,0.06)', transition: 'box-shadow 160ms ease' }}
                    />
                  ))}
                </div>
              </Section>
              <button type="button" onClick={() => onRewriteCopy && onRewriteCopy()} style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BLUE }}>Rewrite copy</button>
            </>
          ) : (
            // image / post
            <>
              <Section title="Prompt">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  placeholder="Describe the image…"
                  style={{ width: '100%', resize: 'vertical', fontFamily: FONT, fontSize: 14, lineHeight: 1.45, color: TEXT2, padding: 10, borderRadius: RADIUS_CTRL, border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.6)', outline: 'none' }}
                />
              </Section>
              <Section title="Aspect ratio">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {ASPECTS.map(a => {
                    const active = aspect === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => { setAspect(a.id); onAction && onAction({ type: 'action', action: 'set_format', value: a.id }); }}
                        style={{ height: 36, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, borderRadius: RADIUS_CTRL, color: active ? '#fff' : TEXT2, background: active ? BLUE : 'rgba(255,255,255,0.6)', border: active ? `1px solid ${BLUE}` : `1px solid ${BORDER}`, transition: 'background 160ms ease, color 160ms ease' }}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </Section>
              <Section title="Style">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {STYLES.map(s => {
                    const active = style === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setStyle(s); onAction && onAction({ type: 'action', action: 'set_style', value: s }); }}
                        style={{ height: 32, padding: '0 12px', cursor: 'pointer', fontFamily: FONT, fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-pill)', color: active ? '#fff' : TEXT2, background: active ? BLUE : 'rgba(255,255,255,0.6)', border: active ? `1px solid ${BLUE}` : `1px solid ${BORDER}`, transition: 'background 160ms ease, color 160ms ease' }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </Section>
              {images.length > 0 && (
                <Section title="Variations">
                  <ThumbGrid images={images} activeIdx={activeIdx} onSelect={selectVariation} />
                </Section>
              )}
            </>
          )}
          </div>
        </aside>

        {/* Tweaks slide-out — quick style adjustments + an AI tweak request */}
        {tweaksOpen && isWebsite && (
          <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 8, width: 244, borderRadius: 14, border: `1px solid ${BORDER}`, padding: 14, boxShadow: '0 16px 40px -16px rgba(15,23,42,0.35)', ...GLASS }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Label>Tweaks</Label>
              <button type="button" aria-label="Close tweaks" onClick={() => setTweaksOpen(false)} style={{ width: 24, height: 24, borderRadius: 7, border: 'none', background: 'transparent', color: TEXT2, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: TEXT2, marginBottom: 6 }}>Font size — {fontScale}%</div>
              <input type="range" min={80} max={140} step={5} value={fontScale} onChange={(e) => setFontScale(Number(e.target.value))} style={{ width: '100%', accentColor: BLUE }} />
            </div>

            <div>
              <div style={{ fontSize: 12, color: TEXT2, marginBottom: 6 }}>Ask Vella to tweak</div>
              <textarea
                value={tweakText}
                onChange={(e) => setTweakText(e.target.value)}
                rows={2}
                placeholder="e.g. Make the hero bigger"
                style={{ width: '100%', boxSizing: 'border-box', resize: 'none', fontFamily: FONT, fontSize: 13, color: TEXT2, padding: 8, borderRadius: RADIUS_CTRL, border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.7)', outline: 'none' }}
              />
              <button
                type="button"
                onClick={() => { const t = tweakText.trim(); if (t) { onAction && onAction({ type: 'action', action: 'ai_tweak', value: t }); setTweakText(''); setTweaksOpen(false); } }}
                style={{ marginTop: 8, width: '100%', height: 34, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#fff', background: BLUE, border: `1px solid ${BLUE}`, borderRadius: RADIUS_CTRL }}
              >
                Send tweak to Vella
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast — Present "Copied!" etc. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{ position: 'absolute', bottom: 72, left: '50%', transform: 'translateX(-50%)', zIndex: 20, background: '#0f2932', color: '#fff', borderRadius: 12, padding: '8px 14px', fontSize: 13, fontWeight: 500, fontFamily: FONT, boxShadow: '0 12px 32px -8px rgba(15,41,50,0.45)', whiteSpace: 'nowrap', animation: 'slideUp 0.2s ease' }}
        >
          {toast}
        </div>
      )}

      {/* Footer — Regenerate (outline) · Publish/Launch (filled), right-aligned */}
      <div style={{ flex: '0 0 auto', height: isWebsite ? 56 : undefined, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: isWebsite ? '0 20px' : '12px 20px', borderTop: `1px solid ${BORDER}` }}>
        <FooterBtn height={isWebsite ? 40 : 40} onClick={() => onRegenerate && onRegenerate()}>Regenerate</FooterBtn>
        {isWebsite ? (
          <>
            <FooterBtn onClick={() => onAction && onAction({ type: 'action', action: 'edit_site' })}>Open &amp; edit</FooterBtn>
            <FooterBtn primary onClick={() => onAction && onAction({ type: 'action', action: 'publish_site' })}>Publish</FooterBtn>
          </>
        ) : (
          <FooterBtn primary onClick={() => onAction && onAction({ type: 'action', action: 'launch_creative' })}>Launch</FooterBtn>
        )}
      </div>
    </aside>
  );
}
